export type ChartTooltipShiftParams = {
  pointX: number;
  tooltipWidth: number;
  boundaryWidth: number;
  gap?: number;
};

export const getChartTooltipTranslateX = ({
  pointX,
  tooltipWidth,
  boundaryWidth,
  gap = 8,
}: ChartTooltipShiftParams) => {
  const halfWidth = tooltipWidth / 2;
  const minCenterX = halfWidth + gap;
  const maxCenterX = boundaryWidth - halfWidth - gap;

  if (boundaryWidth <= tooltipWidth + gap * 2) {
    return -pointX + boundaryWidth / 2;
  }

  const clampedCenterX = Math.max(minCenterX, Math.min(pointX, maxCenterX));

  return clampedCenterX - pointX;
};

export const getLineChartPointX = ({
  index,
  initialSpacing,
  spacing,
}: {
  index: number;
  initialSpacing: number;
  spacing: number;
}) => initialSpacing + index * spacing;

export const getBarChartPointX = ({
  index,
  initialSpacing,
  barWidth,
  spacing,
}: {
  index: number;
  initialSpacing: number;
  barWidth: number;
  spacing: number;
}) => initialSpacing + index * (barWidth + spacing) + barWidth / 2;

/** Mirrors gifted-charts RenderBars bar height (positive values). */
export const getBarHeightFromValue = (
  value: number,
  maxValue: number,
  plotHeight: number,
  minHeight = 4,
) => {
  if (maxValue <= 0) {
    return minHeight;
  }
  return Math.max(minHeight, (Math.abs(value) / maxValue) * plotHeight);
};

/**
 * Gifted anchors bar tooltips with `bottom: barHeight + 2` (grows upward).
 * Shift down when the tooltip would extend above the plot area.
 */
export const getBarChartTooltipTranslateY = ({
  barHeight,
  plotHeight,
  tooltipHeight,
  gap = 8,
}: {
  barHeight: number;
  plotHeight: number;
  tooltipHeight: number;
  gap?: number;
}) => {
  const spaceAboveBar = plotHeight - barHeight;
  const neededAbove = tooltipHeight + gap;
  const overflow = neededAbove - spaceAboveBar;
  return Math.max(0, overflow);
};

/** Rough height matching ChartTooltip padding + header + rows. */
export const estimateChartTooltipHeight = (
  itemCount: number,
  hasHeader = true,
) => {
  const padding = 12;
  const header = hasHeader ? 30 : 0;
  const row = 20;
  return padding + header + Math.max(itemCount, 1) * row;
};
